import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { DataTable, DataTableStateEvent, SortOrder } from "primereact/datatable";
import { Column } from "primereact/column";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/solid";
import { PencilIcon } from "@heroicons/react/24/outline";
import { Button } from "@headlessui/react";
import { Loading } from "../../components/Loading";
import SimplePagination from "../../components/SimplePagination";
import { CustomSortIcon } from "../../components/DatatableHelpers";
import FormattedDate from "../../components/FormattedDate";
import { SimpleOption } from "../../components/input/SimpleListbox";
import MultiSelectDropDown from "../../components/input/MultiSelectDropDown";
import OverflowChips from "../../components/OverflowChips";
import { AlertLevel } from "../../components/AlertMessage";
import ButtonWithConfirm from "../../components/ButtonWithConfirm";
import { roleDescriptions, UserRole } from "../../app/userSlice";
import { roleColors } from "../users/usersApiSlice";
import {
	BroadcastAlertDistrictViewModel,
	BroadcastAlertSchoolViewModel,
	BroadcastAlertViewModel,
	useDeleteAdminAlertsMutation,
	useGetAdminAlertsQuery,
} from "./alertsApiSlice";
import { AddEditBroadcastAlert } from "./AddEditBroadcastAlert";

interface AlertModel extends BroadcastAlertViewModel {
	isChecked: boolean;
}

export default function Alerts() {
	const [alerts, setAlerts] = useState<AlertModel[]>([]);
	const [districtFilter, setDistrictFilter] = useState<string[]>([]);
	const [schoolFilter, setSchoolFilter] = useState<string[]>([]);
	const [rolesFilter, setRolesFilter] = useState<string[]>([]);
	const [sortField, setSortField] = useState("");
	const [sortOrder, setSortOrder] = useState<SortOrder>(0);
	const [addEditAlertDialogOpen, setAddAlertDialogOpen] = useState(false);
	const [editAlertId, setEditAlertId] = useState<number>();
	const [page, setPage] = useState(0);

	const result = useGetAdminAlertsQuery();
	const { data, isLoading } = result;

	const [deleteAlerts] = useDeleteAdminAlertsMutation();

	// Flattened, deduped, sorted schools.
	const schoolOptions = [
		...new Map(alerts.flatMap((a) => a.schools).map((s) => [s.id, s])).values(),
	].sort((a, b) => a.name.localeCompare(b.name));

	useEffect(() => {
		const aaData = data?.aaData;
		if (!aaData) return;

		setAlerts((prev) => {
			const checkedById = new Map(prev.map((a) => [a.dT_RowId, a.isChecked]));
			return aaData.map((a) => ({
				...a,
				isChecked: checkedById.get(a.dT_RowId) ?? false,
			}));
		});
	}, [data?.aaData]);

	useEffect(() => {
		setPage(0);
	}, [districtFilter.length, schoolFilter.length, rolesFilter.length]);

	const handleSort = (event: DataTableStateEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder ?? 0);
		setPage(0);
	};

	function handleSelectOne(e: ChangeEvent<HTMLInputElement>) {
		const id = e.currentTarget.value;
		const isChecked = e.currentTarget.checked;

		setAlerts((prev) => prev.map((a) => (String(a.dT_RowId) === id ? { ...a, isChecked } : a)));
	}

	function handleSelectAll(isChecked: boolean) {
		const filteredAlertIds = new Set(filteredAlerts.map((a) => a.dT_RowId));

		setAlerts((prev) =>
			prev.map((alert) =>
				filteredAlertIds.has(alert.dT_RowId) ? { ...alert, isChecked } : alert
			)
		);
	}

	function handleDeleteAlerts() {
		const checkedAlertIds = alerts
			.filter((alert) => alert.isChecked)
			.map((alert) => alert.dT_RowId);

		deleteAlerts(checkedAlertIds);
	}

	const dateBodyTemplate = (date?: Date) => {
		return !date ? null : (
			<span>
				<FormattedDate date={date} />
			</span>
		);
	};

	const rolesBodyTemplate = (roles: UserRole[], rowIndex: number, rowCount: number) => {
		return (
			<OverflowChips
				items={roles}
				rowIndex={rowIndex}
				rowCount={rowCount}
				getKey={(r) => r}
				chip={(role) => (
					<span
						className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${roleColors[role]}`}>
						{roleDescriptions[role]}
					</span>
				)}
			/>
		);
	};

	const districtListBodyTemplate = (
		entities: BroadcastAlertDistrictViewModel[],
		rowIndex: number,
		rowCount: number
	) => {
		return (
			<OverflowChips
				items={entities}
				rowIndex={rowIndex}
				rowCount={rowCount}
				getKey={(e) => `${e.name}-${e.id}-chip`}
				chip={(entity) => (
					<span className="px-2 py-0.5 text-xs font-medium bg-gray-100 rounded-full whitespace-nowrap">
						{entity.name}
					</span>
				)}
			/>
		);
	};

	const schoolListBodyTemplate = (
		entities: BroadcastAlertSchoolViewModel[],
		rowIndex: number,
		rowCount: number
	) => {
		return (
			<OverflowChips
				items={entities}
				rowIndex={rowIndex}
				rowCount={rowCount}
				getKey={(e) => `${e.name}-${e.districtName}-${e.id}-chip`}
				chip={(entity) => (
					<span className="px-2 py-0.5 text-xs font-medium bg-gray-100 rounded-full whitespace-nowrap">
						{getSchoolDisplayName(entity, schoolOptions)}
					</span>
				)}
			/>
		);
	};

	const messageBodyTemplate = (row: BroadcastAlertViewModel) => {
		return (
			<div className="whitespace-pre-wrap break-words line-clamp-5 hover:line-clamp-none">
				{row.message}
			</div>
		);
	};

	const editBodyTemplate = (row: BroadcastAlertViewModel) => {
		return (
			<button
				id={`edit-${row.dT_RowId}-button`}
				className="mr-2 cursor-pointer"
				onClick={() => {
					setEditAlertId(row.dT_RowId);
					setAddAlertDialogOpen(true);
				}}>
				<PencilIcon className="size-5" />
			</button>
		);
	};

	// Natural sorting for districts and schools.
	const sortByName = (a: SimpleOption, b: SimpleOption) =>
		a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" });

	const districtPool = useMemo<SimpleOption[]>(() => {
		return Array.from(
			new Map(
				alerts
					.flatMap((a) => a.districts)
					.map((s) => [s.id, { value: s.id, label: s.name }])
			).values()
		).sort(sortByName);
	}, [alerts]);

	const schoolPool = useMemo<SimpleOption[]>(() => {
		return Array.from(
			new Map(
				schoolOptions.map((s) => [
					s.id,
					{ value: s.id, label: getSchoolDisplayName(s, schoolOptions) },
				])
			).values()
		).sort(sortByName);
	}, [alerts]);

	const rolesPool = useMemo<SimpleOption[]>(() => {
		const orderedRoles = Object.keys(roleDescriptions) as UserRole[];

		return Array.from(new Set(alerts.flatMap((a) => a.roles)))
			.map((role) => ({
				value: role,
				label: roleDescriptions[role],
			}))
			.sort(
				(a, b) =>
					orderedRoles.indexOf(a.value as UserRole) -
					orderedRoles.indexOf(b.value as UserRole)
			);
	}, [alerts]);

	const isFiltered = !!districtFilter.length || !!schoolFilter.length || !!rolesFilter.length;

	const filteredAlerts = useMemo(
		() =>
			alerts.filter(
				(a) =>
					(!districtFilter.length ||
						a.districts.some((d) => districtFilter.includes(d.id))) &&
					(!schoolFilter.length || a.schools.some((s) => schoolFilter.includes(s.id))) &&
					(!rolesFilter.length || a.roles.some((r) => rolesFilter.includes(r)))
			),
		[alerts, districtFilter, schoolFilter, rolesFilter]
	);

	const sortedAndFilteredAlerts = useMemo(() => {
		if (!sortField || sortOrder === 0) {
			return filteredAlerts;
		}

		return [...filteredAlerts].sort((a, b) => {
			let aValue: any = a[sortField as keyof AlertModel];
			let bValue: any = b[sortField as keyof AlertModel];

			// Convert date strings to Date objects for proper comparison
			if (sortField === "beginDate" || sortField === "expireDate") {
				aValue = aValue ? new Date(aValue).getTime() : null;
				bValue = bValue ? new Date(bValue).getTime() : null;
			}

			// Handle null/undefined values - always place nulls at the end
			if (aValue == null && bValue == null) return 0;
			if (aValue == null) return 1; // a (null) goes after b
			if (bValue == null) return -1; // b (null) goes after a

			// Compare values
			let comparison = 0;
			if (typeof aValue === "string" && typeof bValue === "string") {
				comparison = aValue.localeCompare(bValue);
			} else if (aValue < bValue) {
				comparison = -1;
			} else if (aValue > bValue) {
				comparison = 1;
			}

			return sortOrder === 1 ? comparison : -comparison;
		});
	}, [filteredAlerts, sortField, sortOrder]);

	const pageSize = 10;
	const paginatedAlerts = useMemo(
		() => sortedAndFilteredAlerts.slice(page * pageSize, (page + 1) * pageSize),
		[sortedAndFilteredAlerts, page, pageSize]
	);

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	const allChecked = filteredAlerts.length > 0 && filteredAlerts.every((a) => a.isChecked);
	const anyChecked = alerts.length > 0 && alerts.some((a) => a.isChecked);
	const numChecked = alerts
		.filter((alert) => alert.isChecked)
		.map((alert) => alert.dT_RowId).length;

	return (
		<section>
			<h1 className="text-s sm:text-left w-full">Broadcast Alerts</h1>
			<div className="pt-4 flex justify-between items-end">
				<div className="flex gap-4">
					{rolesPool?.length > 0 && (
						<MultiSelectDropDown
							values={rolesFilter}
							setValues={(vals) => setRolesFilter(vals as string[])}
							label="Roles"
							id="rolesFilter"
							options={rolesPool}
							fieldClasses="w-64"
							renderOption={(opt) => (
								<div className="flex gap-2 items-center">
									<span
										className={`block w-3 h-3 rounded-full ${roleColors[opt.value as UserRole]}`}></span>
									<span>{opt.label}</span>
								</div>
							)}
						/>
					)}
					{districtPool?.length > 0 && (
						<MultiSelectDropDown
							values={districtFilter}
							setValues={(vals) => setDistrictFilter(vals as string[])}
							label="Districts"
							id="districtsFilter"
							options={districtPool}
							fieldClasses="w-64"
						/>
					)}
					{schoolPool?.length > 0 && (
						<MultiSelectDropDown
							values={schoolFilter}
							setValues={(vals) => setSchoolFilter(vals as string[])}
							label="Schools"
							id="schoolsFilter"
							options={schoolPool}
							fieldClasses="w-64"
						/>
					)}
				</div>
				<div className="flex gap-2">
					<Button
						id="add-broadcast-alert-button"
						className="btn btn-primary h-fit w-fit whitespace-nowrap"
						onClick={() => setAddAlertDialogOpen(true)}>
						<span className="flex items-center">
							<PlusIcon className="size-4 mr-2" /> Add Alert
						</span>
					</Button>
					{anyChecked && (
						<ButtonWithConfirm
							id="add-broadcast-alert-button"
							className="btn btn-neutral h-fit w-fit whitespace-nowrap"
							type={AlertLevel.Warning}
							message={`Are you sure you want to remove ${numChecked} alert${numChecked > 1 ? "s" : ""}?`}
							confirmLabel="Yes"
							cancelLabel="No"
							onConfirm={handleDeleteAlerts}>
							<span className="flex items-center">
								<TrashIcon className="size-4 mr-2" /> Remove Alerts
							</span>
						</ButtonWithConfirm>
					)}
				</div>
			</div>
			<Loading loading={isLoading} />
			{data && (
				<DataTable
					value={paginatedAlerts}
					dataKey="dT_RowId"
					emptyMessage="No broadcast alerts found"
					stripedRows
					sortField={sortField}
					sortOrder={sortOrder}
					onSort={handleSort}
					sortIcon={CustomSortIcon}
					className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
					style={{ paddingTop: "16px" }}>
					<Column
						header={
							<input
								type="checkbox"
								checked={allChecked}
								onChange={(e) => handleSelectAll(e.target.checked)}
								className="cursor-pointer size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
								aria-label="Select all alerts"
							/>
						}
						body={(row: AlertModel) => (
							<input
								type="checkbox"
								value={row.dT_RowId}
								checked={row.isChecked}
								onChange={handleSelectOne}
								className="cursor-pointer size-4 rounded-sm border-gray-300 text-primary-600 focus:ring-primary-600"
								aria-label={`Select alert: ${row.message}`}
							/>
						)}
						className="w-12"
						headerClassName="text-center"
						bodyClassName="text-center"
					/>
					<Column
						header="Message"
						field="message"
						sortable
						sortField="message"
						body={messageBodyTemplate}
						className="max-w-[300px]"
					/>
					<Column
						header="Roles"
						body={(row: BroadcastAlertViewModel, options: any) =>
							rolesBodyTemplate(
								row.roles,
								options.rowIndex,
								sortedAndFilteredAlerts.length
							)
						}
						field="role"
						className="max-w-[200px]"
					/>
					<Column
						header="Districts"
						body={(row: BroadcastAlertViewModel, options: any) =>
							districtListBodyTemplate(
								row.districts,
								options.rowIndex,
								sortedAndFilteredAlerts.length
							)
						}
						field="districts"
						className="max-w-[200px]"
					/>
					<Column
						header="Schools"
						body={(row: BroadcastAlertViewModel, options: any) =>
							schoolListBodyTemplate(
								row.schools,
								options.rowIndex,
								sortedAndFilteredAlerts.length
							)
						}
						field="schools"
						className="max-w-[200px]"
					/>
					<Column
						header="Start Date"
						body={(row: BroadcastAlertViewModel) => dateBodyTemplate(row.beginDate)}
						field="beginDate"
						sortable
						sortField="beginDate"
					/>
					<Column
						header="End Date"
						body={(row: BroadcastAlertViewModel) => dateBodyTemplate(row.expireDate)}
						field="expireDate"
						sortable
						sortField="expireDate"
					/>
					<Column body={editBodyTemplate} className="!pl-1 !pr-2" />
				</DataTable>
			)}
			<SimplePagination
				itemCount={filteredAlerts.length}
				page={page}
				pageSize={pageSize}
				totalCount={alerts.length}
				isFiltered={isFiltered}
				onChange={(page) => handlePageChange(page)}
				className={"border-none"}
			/>
			{addEditAlertDialogOpen && (
				<AddEditBroadcastAlert
					dialogOpen={addEditAlertDialogOpen}
					setDialogOpen={(open) => {
						setAddAlertDialogOpen(open);
						if (!open) {
							setEditAlertId(undefined);
						}
					}}
					editId={editAlertId}
				/>
			)}
		</section>
	);
}

// Helper function to determine school display name based on duplicates
export const getSchoolDisplayName = (
	school: BroadcastAlertSchoolViewModel,
	allSchools: BroadcastAlertSchoolViewModel[]
): string => {
	const schoolsWithSameName = allSchools.filter(
		(s) => s.name.trim().toLowerCase() === school.name.trim().toLowerCase()
	);

	if (schoolsWithSameName.length === 1) {
		// No duplicates, just show the name
		return school.name;
	}

	const uniqueDistrictIds = new Set(schoolsWithSameName.map((s) => s.districtId));
	const hasDifferentDistricts = uniqueDistrictIds.size > 1;

	const schoolsInSameDistrict = schoolsWithSameName.filter(
		(s) => s.districtId === school.districtId
	);
	const hasSameDistrictDuplicates = schoolsInSameDistrict.length > 1;

	const districtName = school.districtName ?? "No District";

	if (hasDifferentDistricts && hasSameDistrictDuplicates) {
		// Same name in same district AND across different districts
		return `${school.name} - ${districtName} (ID ${school.id})`;
	} else if (hasSameDistrictDuplicates) {
		// Same name in the same district
		return `${school.name} (ID ${school.id})`;
	} else {
		// Same name across different districts
		return `${school.name} - ${districtName}`;
	}
};
